import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Alert, CircularProgress, Divider, Stack,
  useMediaQuery, useTheme,
} from '@mui/material';
import { CheckCircle as OkIcon, Error as ErrIcon, HourglassEmpty as WaitIcon } from '@mui/icons-material';
import { aprovadorApi, PanelItem, Review, AprovadorError, brl, ApiLimit, Saldo } from '@/services/aprovador';

interface Props {
  token: string;
  items: PanelItem[] | null;
  onClose: () => void;
  onDone: () => void;
  onSessionLost: () => void;
}

type Linha = {
  item: PanelItem;
  review?: Review;
  erro?: string;
  pago?: boolean;
  estado: 'aguardando' | 'conferindo' | 'conferido' | 'erro' | 'pagando' | 'pago' | 'falhou';
};

// O plano do Sicoob aceita ~1 req/s: espaçar as chamadas que vão ao banco.
const ESPACO_MS = 3000;
const esperar = (ms: number) => new Promise((r) => setTimeout(r, ms));

type Banco = 'sicoob' | 'efi';
const NOME: Record<Banco, string> = { sicoob: 'Sicoob', efi: 'Efí' };
const NA: Record<Banco, string> = { sicoob: 'no Sicoob', efi: 'na Efí' };
const DA: Record<Banco, string> = { sicoob: 'do Sicoob', efi: 'da Efí' };
// Boleto e guia saem da Efí quando a empresa tem conta lá; o resto (e a transferência p/ a Efí) sai do Sicoob.
const bancoDe = (r?: Review): Banco => (r?.bank === 'efi' && !r.transferToEfi ? 'efi' : 'sicoob');

/** Aprovar vários PIX: confere cada recebedor no banco → lista para conferência → paga um por um. */
const BulkApproveDialog: React.FC<Props> = ({ token, items, onClose, onDone, onSessionLost }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [fase, setFase] = useState<'conferindo' | 'pronto' | 'pagando' | 'fim'>('conferindo');
  const [saldos, setSaldos] = useState<Partial<Record<Banco, Saldo>>>({});
  const [teto, setTeto] = useState<ApiLimit | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const vivo = useRef(true);

  const set = (id: string, patch: Partial<Linha>) =>
    setLinhas((ls) => ls.map((l) => (l.item.id === id ? { ...l, ...patch } : l)));

  useEffect(() => {
    if (!items || !items.length) return undefined;
    vivo.current = true;
    setLinhas(items.map((item) => ({ item, estado: 'aguardando' })));
    setFase('conferindo'); setAviso(null); setSaldos({}); setTeto(null);
    (async () => {
      for (let k = 0; k < items.length; k += 1) {
        if (!vivo.current) return;
        const it = items[k];
        set(it.id, { estado: 'conferindo' });
        try {
          const r = await aprovadorApi.conferir(token, it.id);
          set(it.id, { estado: 'conferido', review: r });
          setSaldos((s) => ({ ...s, [bancoDe(r)]: r.saldo }));
          if (r.apiLimit) setTeto(r.apiLimit);
        } catch (e) {
          const err = e as AprovadorError;
          if (err.status === 401) { onSessionLost(); return; }
          set(it.id, { estado: 'erro', erro: err.message });
        }
        if (k < items.length - 1 && !it.awaitingConfirmation) await esperar(ESPACO_MS);
      }
      if (vivo.current) setFase('pronto');
    })();
    return () => { vivo.current = false; };
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const prontos = linhas.filter((l) => l.estado === 'conferido' && l.review);
  const total = prontos.reduce((s, l) => s + (l.review?.valor || 0), 0);
  const totalDe = (b: Banco) => prontos.filter((l) => bancoDe(l.review) === b).reduce((s, l) => s + (l.review?.valor || 0), 0);
  const bancos = (['sicoob', 'efi'] as Banco[]).filter((b) => prontos.some((l) => bancoDe(l.review) === b));
  const bancosSemSaldo = bancos.filter((b) => saldos[b]?.disponivel != null && totalDe(b) > (saldos[b]?.disponivel as number));
  const semSaldo = bancosSemSaldo.length > 0;
  // O teto diário é da API do Sicoob: o que sai pela Efí não conta.
  const semTeto = teto != null && totalDe('sicoob') > teto.available;

  const pagar = async () => {
    setFase('pagando');
    for (let k = 0; k < prontos.length; k += 1) {
      if (!vivo.current) return;
      const l = prontos[k];
      set(l.item.id, { estado: 'pagando' });
      try {
        const r = await aprovadorApi.confirmar(token, l.item.id);
        set(l.item.id, r.ok ? { estado: 'pago' } : { estado: 'falhou', erro: r.error || 'Falha ao pagar' });
      } catch (e) {
        const err = e as AprovadorError;
        if (err.status === 401) { onSessionLost(); return; }
        set(l.item.id, { estado: 'falhou', erro: err.message });
        if (err.code === 'insufficient_balance' || err.code === 'daily_limit') {
          setAviso(`${err.message} Os seguintes não foram pagos.`);
          break;
        }
      }
      if (k < prontos.length - 1) await esperar(ESPACO_MS);
    }
    setFase('fim');
    onDone();
  };

  const fechar = () => { if (fase === 'pagando') return; vivo.current = false; if (fase === 'fim') onDone(); onClose(); };

  const icone = (l: Linha) => {
    if (l.estado === 'pago') return <OkIcon color="success" fontSize="small" />;
    if (l.estado === 'erro' || l.estado === 'falhou') return <ErrIcon color="error" fontSize="small" />;
    if (l.estado === 'conferindo' || l.estado === 'pagando') return <CircularProgress size={16} />;
    if (l.estado === 'conferido') return <OkIcon color="primary" fontSize="small" />;
    return <WaitIcon color="disabled" fontSize="small" />;
  };

  const pagos = linhas.filter((l) => l.estado === 'pago');

  return (
    <Dialog open={!!items && items.length > 0} onClose={fechar} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {fase === 'conferindo' ? 'Conferindo os recebedores no banco…'
          : fase === 'fim' ? 'Resultado' : `Confira os ${prontos.length} PIX`}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Stack divider={<Divider />}>
          {linhas.map((l) => (
            <Box key={l.item.id} sx={{ display: 'flex', gap: 1.5, px: 2, py: 1.25, alignItems: 'flex-start' }}>
              <Box sx={{ pt: 0.25 }}>{icone(l)}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                  {l.review?.recebedor.nome || l.item.beneficiaryName || l.item.pixKey}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  {l.review?.recebedor.documento ? `CPF/CNPJ ${l.review.recebedor.documento} · ` : ''}
                  {l.item.description || l.item.beneficiaryName || ''}
                </Typography>
                {l.erro && <Typography variant="caption" color="error.main" component="div">{l.erro}</Typography>}
              </Box>
              <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {brl(l.review?.valor ?? l.item.amount)}
              </Typography>
            </Box>
          ))}
        </Stack>
        <Box sx={{ px: 2, py: 1.5, bgcolor: '#fafbfc', borderTop: '1px solid #eef1f5' }}>
          {fase !== 'fim' ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Total a pagar</Typography>
                <Typography variant="body1" fontWeight={700}>{brl(total)}</Typography>
              </Box>
              {bancos.map((b) => (
                <React.Fragment key={b}>
                  {bancos.length > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Sai {DA[b]}</Typography>
                      <Typography variant="caption">{brl(totalDe(b))}</Typography>
                    </Box>
                  )}
                  {saldos[b]?.disponivel != null && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Saldo disponível {NA[b]}</Typography>
                      <Typography variant="caption">{brl(saldos[b]?.disponivel)}</Typography>
                    </Box>
                  )}
                </React.Fragment>
              ))}
              {teto && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">Cabe no teto da API hoje</Typography>
                  <Typography variant="caption">{brl(teto.available)}</Typography>
                </Box>
              )}
            </>
          ) : (
            <Typography variant="body2">{pagos.length} de {linhas.length} pago{pagos.length === 1 ? '' : 's'}. Os comprovantes estão em "Pagos hoje" e o grupo foi avisado.</Typography>
          )}
          {fase === 'pronto' && semSaldo && (
            <Alert severity="error" sx={{ mt: 1 }}>
              O que sai {bancosSemSaldo.map((b) => DA[b]).join(' e ')} passa do saldo {bancosSemSaldo.length > 1 ? 'dessas contas' : 'dessa conta'}. Desmarque alguns e tente de novo
              {bancosSemSaldo.includes('efi') ? ', ou leve dinheiro do Sicoob pelo botão “Transferir para a Efí”.' : '.'}
            </Alert>
          )}
          {fase === 'pronto' && semTeto && <Alert severity="error" sx={{ mt: 1 }}>O total passa do teto diário da API. Desmarque alguns ou pague no app.</Alert>}
          {fase === 'pronto' && linhas.some((l) => l.estado === 'erro') && (
            <Alert severity="warning" sx={{ mt: 1 }}>Os itens com erro ficam de fora e continuam pendentes.</Alert>
          )}
          {aviso && <Alert severity="error" sx={{ mt: 1 }}>{aviso}</Alert>}
          {(fase === 'pronto') && prontos.length > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Ao confirmar, os pagamentos saem {bancos.length > 1 ? 'das contas Sicoob e Efí' : `da conta ${NOME[bancos[0]]}`} um a um, com alguns segundos entre eles. <b>Não dá para desfazer.</b>
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1, pb: 'calc(16px + env(safe-area-inset-bottom))' }}>
        {fase === 'fim' ? (
          <Button fullWidth variant="contained" onClick={fechar}>Fechar</Button>
        ) : (
          <>
            <Button onClick={fechar} disabled={fase === 'pagando'}>Cancelar</Button>
            <Button variant="contained" color="success" sx={{ flex: 1 }} onClick={pagar}
              disabled={fase !== 'pronto' || prontos.length === 0 || semSaldo || semTeto}>
              {fase === 'pagando' ? <CircularProgress size={22} color="inherit" /> : `Pagar ${prontos.length} PIX · ${brl(total)}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkApproveDialog;
